import { fetcher } from '@commenda/utils';
import { Currency, StockClassType } from '@prisma/client';
import { useRouter } from 'next/router';
import { useState } from 'react';
import {
  FieldWrapper,
  Modal,
  PrimaryButton,
  SelectInput,
  TextInput,
} from '../../../components';

type StockClassModalProps = {
  token: string;
  isAddStockClassModalShown: boolean;
  toggleAddStockClassModalVisibility: () => void;
  mutate: () => void;
  setNotification: (arg0) => void;
  currency: Currency;
};

const defaultInitialAuthorizedShares = '10000000';
const defaultPricePerShare = '0.00001';

const AddStockClassModal = ({
  token,
  isAddStockClassModalShown,
  toggleAddStockClassModalVisibility,
  mutate,
  setNotification,
  currency,
}: StockClassModalProps) => {
  const router = useRouter();

  const { id } = router.query;

  const [selectedStockClassType, setSelectedStockClassType] = useState(null);
  const [stockClassName, setStockClassName] = useState('');

  const [noOfInitialAuthorisedShares, setNoOfInitialAuthorisedShares] =
    useState(defaultInitialAuthorizedShares);
  const [pricePerShare, setPricePerShare] = useState(defaultPricePerShare);

  const [isEditingAuthorizedShares, setIsEditingAuthorizedShares] =
    useState(false);

  const [isEditingPricePerShare, setIsEditingPricePerShare] = useState(false);

  const stockClassTypeOptions = [
    { displayedValue: 'Common', value: StockClassType.COMMON },
    { displayedValue: 'Preferred', value: StockClassType.PREFERRED },
  ];

  const onAddStockClass = async () => {
    if (!stockClassName) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Please enter a share class name',
      });
      return false;
    }

    if (!selectedStockClassType) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Please select a share class type',
      });
      return false;
    }

    if (!noOfInitialAuthorisedShares) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Please enter number of Authorized shares.',
      });
      return false;
    }

    if (!pricePerShare) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Please enter price of each share',
      });
      return false;
    }

    const body = {
      type: selectedStockClassType,
      name: stockClassName,
      corporationId: +(id as string),
      additionalInformation: {
        authorizedSharesType: 'Not Applicable',
        authorizedShares: +noOfInitialAuthorisedShares,
        pricePerShare: {
          amount: +pricePerShare,
          currency,
        },
      },
    };

    const response = await fetcher(
      'capTable/stockClass',
      {
        method: 'POST',
        body,
      },
      token
    );

    if (!response.ok) {
      setNotification({
        valid: false,
        show: true,
        title: 'Error',
        message: 'Please try again, or contact Team Commenda if you need help',
      });
      return false;
    }

    await mutate();

    toggleAddStockClassModalVisibility();

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Successfully added stock class',
    });

    setNoOfInitialAuthorisedShares(defaultInitialAuthorizedShares);
    setStockClassName(null);
    setPricePerShare(defaultPricePerShare);

    return true;
  };

  return (
    <Modal
      isShown={isAddStockClassModalShown}
      hide={() => {
        toggleAddStockClassModalVisibility();
        setIsEditingAuthorizedShares(false);
        setIsEditingPricePerShare(false);
      }}
      headerText="Add Share Class"
      additionalClassName="min-w-[520px]"
    >
      <div className="flex flex-col gap-3">
        <FieldWrapper label="Choose Share Class Type" id="emailAddress">
          <SelectInput
            options={stockClassTypeOptions}
            value={selectedStockClassType}
            setValue={setSelectedStockClassType}
          />
        </FieldWrapper>

        <FieldWrapper label="Share Class Name" id="stockClassName">
          <TextInput
            type="text"
            id="stockClassName"
            placeholder="Enter Share Class Name"
            value={stockClassName}
            setValue={setStockClassName}
          />
        </FieldWrapper>
        <FieldWrapper
          label="Authorized Shares"
          id="noOfInitialAuthorisedShares"
        >
          {isEditingAuthorizedShares ? (
            // empty paragraphs to maintain spacing:
            <p className="text-xs">
              <br />
              <br />
            </p>
          ) : (
            <p className="text-xs text-gray-700">
              The most common value is suggested below, but you can
              <span
                className="text-blue-700 cursor-pointer"
                onClick={() => setIsEditingAuthorizedShares(true)}
              >
                {' '}
                click here{' '}
              </span>
              to edit
            </p>
          )}
          <TextInput
            type="number"
            id="noOfInitialAuthorisedShares"
            placeholder="Authorized Shares"
            value={noOfInitialAuthorisedShares}
            setValue={(value) => setNoOfInitialAuthorisedShares(value)}
            disabled={!isEditingAuthorizedShares}
          />
        </FieldWrapper>
        <FieldWrapper label="Price Per Share" id="valuesPerShare">
          {isEditingPricePerShare ? (
            // empty paragraphs to maintain spacing:
            <p className="text-xs">
              <br />
              <br />
            </p>
          ) : (
            <p className="text-xs text-gray-700">
              The most common value is suggested below, but you can
              <span
                className="text-blue-700 cursor-pointer"
                onClick={() => setIsEditingPricePerShare(true)}
              >
                {' '}
                click here{' '}
              </span>
              to edit
            </p>
          )}

          <TextInput
            type="number"
            id="valuePerShare"
            placeholder="price per share"
            disabled={!isEditingPricePerShare}
            value={pricePerShare}
            setValue={(value) => setPricePerShare(value)}
          />
        </FieldWrapper>

        <div className="flex gap-4 self-end">
          <PrimaryButton onClick={onAddStockClass}>
            Add Share Class
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
};

export default AddStockClassModal;
